import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import ICart_Item from "../../../models/ICart_Item";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import IPrice_Group from "../../../models/IPrice_Group";
import { EcommerceCartItemQty } from "../EcommerceCartItemQty/EcommerceCartItemQty";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OrderEntryService from "../../../services/OrderEntryService";
import { UomSetSelectOption } from "../../shared/UomSetSelectOption/UomSetSelectOption";
import IUnit_Of_Measurement_Set from "../../../models/IUnit_Of_Measurement_Set";
import IOrder from "../../../models/IOrder";
import SupplierContext from "../../../contexts/SupplierContext";
import { useCart } from "../../../contexts/CartContext";
import { DescriptionExpander } from "../../shared/DescriptionExpander/DescriptionExpander";
import HelperService from "../../../services/HelperService";
import UIService from "@services/UIService";
import "./EcommerceCheckoutCartItem.scss";
import { startCase } from "lodash-es";
import { useAppContextNonNull } from "@contexts/AppContext";
import CatalogService from "@services/CatalogService";
import UserService from "@services/UserService";
import { getPackItemTotalUnits } from "@services/CatalogItemService";

type Props = {
    cartItem: ICart_Item;
    priceGroup?: IPrice_Group;
    updateAddlInput: (item: ICart_Item, addlInput: string) => void;
    updateQty: (item: ICart_Item, qty: number) => void;
    deleteItem: (item: ICart_Item) => void;
    isUpdating: boolean;
    kitBuilderItems?: ICart_Item[];
};

export const EcommerceCheckoutCartItem: React.FC<Props> = (props) => {
    const { supplierModel, userModel, vendorModel } = useAppContextNonNull();
    const { cart } = useCart();
    const hasInitialRender = useRef(false);

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    let currencyName = vendorModel.Catalog?.Currency?.name ? vendorModel.Catalog.Currency.name : "USD";
    if (vendorModel.Catalog?.currency_id) {
        currencyName = CatalogService.getCurrencyNameFromId(vendorModel.Catalog.currency_id);
    }

    const getTotalQty = () => {
        // cartItem.qty is stored in units; convert to total units (no-op for non-pack items).
        return getPackItemTotalUnits(props.cartItem.Catalog_Item, props.cartItem.qty);
    };

    const [totalQty, setTotalQty] = useState(getTotalQty());
    const [addlInput, setAddlInput] = useState(props.cartItem.addl_input);
    const [selectedUnit, setSelectedUnit] = useState<IUnit_Of_Measurement_Set | undefined>(props.cartItem.Selected_Unit);

    useEffect(() => {
        if (selectedUnit && hasInitialRender.current) {
            const qtyDetail = OrderEntryService.doInventoryValidation(
                props.cartItem.Catalog_Item!,
                props.cartItem.qty,
                [],
                supplierModel,
                {} as IOrder,
                0,
                selectedUnit
            );

            updateQty(qtyDetail.qty);
        }
        hasInitialRender.current = true;
    }, [selectedUnit]);

    useEffect(() => {
        setTotalQty(getTotalQty());
    }, [props.cartItem.qty]);

    const priceEach = props.priceGroup
        ? OrderEntryService.getDiscountedPriceForCartItem(props.cartItem, props.priceGroup, supplierModel, cart?.supplierVendorToken)
        : 0;

    const uom = (props.cartItem.Catalog_Item?.Unit_Of_Measurement?.abbreviation || "").toLowerCase();

    let totalPrice = 0;

    if (props.cartItem.qty) {
        totalPrice = props.cartItem.qty * priceEach;
    }

    const updateQty = (qty: number) => {
        const cartItem = { ...props.cartItem };

        if (selectedUnit) {
            cartItem.uom_set_id = selectedUnit.id;
            cartItem.uom_set_qty = qty;
        }

        props.updateQty(cartItem, qty);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        setAddlInput(value);

        props.updateAddlInput(props.cartItem, value);
    };

    const selectedOptionFn = (option: IUnit_Of_Measurement_Set) => {
        setSelectedUnit(option);
    };

    const uomSetQtyConversion = OrderEntryService.getUomSetQty(
        props.cartItem.uom_set_qty || 1,
        props.cartItem.Catalog_Item?.Base_Unit,
        selectedUnit
    );

    return (
        <div className={"EcommerceCheckoutCartItem ecommerceCheckoutCartItem"}>
            {props.isUpdating ? (
                <div className={"updatingOverlay"}>
                    <OwLabel title={"updating"} />
                </div>
            ) : null}
            <div className={"itemContainer"}>
                <div>
                    <OwS3ImageViewer
                        catalogItem={props.cartItem.Catalog_Item}
                        isMicroView={false}
                        attachmentIndex={0}
                        imageIndex={IImageSizeEnum.THUMB}
                        className={"w-5r h-5r"}
                        includeAllImages={false}
                        hideEmptyImage={false}
                    />
                </div>
                <div className={"w-100"}>
                    <div className={"d-flex justify-content-between"}>
                        <div>
                            {props.cartItem.parent_cart_item_id ? (
                                <div className={"text-small text-muted"}>
                                    <OwLabel title={"kit_item"} />
                                </div>
                            ) : null}
                            <b>{props.cartItem.Catalog_Item!.name}</b>
                        </div>
                        {props.cartItem.parent_cart_item_id ? null : (
                            <div className={"ms-auto"}>
                                {!hidePricing
                                    ? HelperService.formatCurrency({ value: totalPrice || 0, precision: 2, currency: currencyName })
                                    : null}

                                <div className={"d-flex ms-auto"}>
                                    <OwIconButton
                                        iconClass={"fad fa-trash-alt"}
                                        iconColorClass={"text-danger ms-auto"}
                                        fn={() => {
                                            props.deleteItem(props.cartItem);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={"text-small"}>{props.cartItem.Catalog_Item!.sku}</div>
                    <div className={"text-small"}>
                        {(props.cartItem.Catalog_Item?.short_description?.length || 0) > 0 ? (
                            <div className={"mt-3 mb-3"}>{props.cartItem.Catalog_Item?.short_description}</div>
                        ) : null}
                        <DescriptionExpander catalogItem={props.cartItem.Catalog_Item!} height={"1rem"} />
                    </div>
                    {props.cartItem.Cart_Item_Options && (
                        <div className={"mt-2 mb-2"}>
                            {props.cartItem.Cart_Item_Options?.map((itemOption) => {
                                return (
                                    <div key={itemOption.item_option_id}>
                                        <b>{itemOption.Item_Option?.name}:</b> {startCase(itemOption.value)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {props.cartItem.parent_cart_item_id || hidePricing ? null : (
                        <div className={"mt-2"}>
                            <div>
                                {HelperService.formatCurrency({ value: priceEach || 0, precision: 2, currency: currencyName })}
                                {uom ? " / " + uom : null}
                            </div>
                        </div>
                    )}
                    <div className={"mt-2"}>
                        {props.cartItem.Catalog_Item?.has_pack_qty ? (
                            <div className={"text-small"}>
                                <OwLabel title={"pack_qty"} postText={":"} /> {props.cartItem.Catalog_Item?.pack_qty}
                            </div>
                        ) : null}
                    </div>
                    <div className={"mt-2"}>
                        <div className={"d-flex"}>
                            <EcommerceCartItemQty
                                cartItem={props.cartItem}
                                updateFn={updateQty}
                                readOnly={!!props.cartItem.parent_cart_item_id}
                                uomSet={selectedUnit}
                            />
                            {props.cartItem.Selected_Unit ? (
                                <div className={"uomSetSelectOptionWrapper"}>
                                    <UomSetSelectOption
                                        item={props.cartItem.Catalog_Item!}
                                        selectedOption={selectedOptionFn}
                                        currentOption={props.cartItem.Selected_Unit}
                                    />
                                </div>
                            ) : null}
                        </div>
                        {uomSetQtyConversion ? (
                            <div className={"mt-2"}>
                                {uomSetQtyConversion} {props.cartItem.Catalog_Item?.Base_Unit?.unit_abbr}
                            </div>
                        ) : null}
                        <div>
                            {props.cartItem.Catalog_Item?.has_pack_qty ? (
                                <div>
                                    <OwLabel title={"total_qty"} postText={":"} className={"me-2"} />
                                    {totalQty}
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {props.cartItem.Catalog_Item?.max_order_qty ? (
                                <div className={"text-small text-danger"}>
                                    <OwLabel title={"max_order_qty"} postText={":"} />
                                    {props.cartItem.Catalog_Item?.max_order_qty}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {props.kitBuilderItems?.length ? (
                        <div className={"mt-2"}>
                            {props.kitBuilderItems
                                ?.filter((item) => item.parent_cart_item_id === props.cartItem.id)
                                .map((kitItem) => {
                                    const subItems = props.kitBuilderItems!.filter((subItem) => subItem.parent_cart_item_id === kitItem.id);

                                    return (
                                        <div key={kitItem.guid} className={"mb-2 border rounded"}>
                                            <div
                                                className={
                                                    (UIService.isMainViewLight() ? "bg-primary text-light" : "bg-warning text-dark") +
                                                    " p-1 rounded-top"
                                                }>
                                                {kitItem.Kit_Builder_Option?.name}
                                            </div>
                                            <div className={"p-1"}>{kitItem.Catalog_Item?.name}</div>
                                            <div className={"p-1 text-muted"}>{kitItem.Catalog_Item?.description}</div>
                                            {subItems?.map((subItem) => {
                                                return (
                                                    <div key={subItem.guid} className={"ms-4 mt-2"}>
                                                        <i className={"fas fa-turn-down-right me-1"} />
                                                        {subItem.Catalog_Item?.name}
                                                        <div className={"text-muted"}>{subItem.Catalog_Item?.description}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                        </div>
                    ) : null}
                    {props.cartItem.Catalog_Item?.require_addl_input ? (
                        <div className="mt-2">
                            <div>{props.cartItem.Catalog_Item?.addl_input_description || ""}</div>
                            <OwFnInput name={"addl_input"} fn={handleChange} value={addlInput} enableDebounce={true} />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
